import { DEFAULT_CONSUMER_PAYMENT_SCHEDULE } from './consumerQuotes';

export const INITIAL_CONSUMER_PLAN_STATE: ConsumerPlanState = {
  careSetting: null,
  household: 'solo',
  partner: null,
  paymentSchedule: DEFAULT_CONSUMER_PAYMENT_SCHEDULE,
  selectedQuoteKey: null,
  application: null,
  lastStep: null,
};

// Partners saved before the full Quick Start questions had only a name and an age.
function isCurrentPartner(partner: Partial<ConsumerPartner> | null | undefined): partner is ConsumerPartner {
  return !!partner?.lastName;
}

/** Fills fields missing from a stored plan so older saves keep working as the plan grows. */
export function withConsumerPlanDefaults(
  stored: Partial<ConsumerPlanState> | null | undefined,
): ConsumerPlanState {
  const plan = { ...INITIAL_CONSUMER_PLAN_STATE, ...(stored ?? {}) };
  return { ...plan, partner: isCurrentPartner(plan.partner) ? plan.partner : null };
}

export const CONSUMER_POLICY_REVIEW_PRICES_IN_CENTS: Record<ConsumerPolicyReviewType, number> = {
  policy: 5000,
  rate: 15000,
};

/** Shown to consumers; must match the self-funding plan's Stripe price. */
export const CONSUMER_SELF_FUND_PRICE_IN_CENTS = 995;

/** How long one self-funding payment gives access for. It doesn't renew. */
export const CONSUMER_SELF_FUND_ACCESS_MONTHS = 12;

// The team sends results by email and the app isn't told when they're done, so a paid review
// counts as in progress for this long (the target is 24–48 hours, at most 7 business days).
const POLICY_REVIEW_IN_PROGRESS_DAYS = 14;

/** A paid review the team is still working on; a second one can't be started meanwhile. */
export function isPolicyReviewInProgress(
  review: { status: ConsumerPolicyReviewStatus; paidAt: Date | string | null } | null | undefined,
  now: Date = new Date(),
): boolean {
  if (review?.status !== 'paid' || !review.paidAt) return false;
  const daysSincePaid = (now.getTime() - new Date(review.paidAt).getTime()) / (24 * 60 * 60 * 1000);
  return daysSincePaid < POLICY_REVIEW_IN_PROGRESS_DAYS;
}

/** The app page our team opens to download one of a policy review's documents. */
export function consumerPolicyReviewDocumentPath(reviewId: string, documentIndex: number): string {
  return `/consumer-policy-reviews/${reviewId}/documents/${documentIndex}`;
}

export const CONSUMER_POLICY_REVIEW_NAMES: Record<ConsumerPolicyReviewType, string> = {
  policy: 'Existing-policy analysis',
  rate: 'Rate-action and reduced-benefit analysis',
};
